import React, { useEffect } from "react";
import { DEFAULT_LABORATORY } from "@app/pages/samples/utils/constants";
import dayjs from "dayjs";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";
import { ProcedureType, UserType } from "@app/types/genericTables.types";
import {
  AnalysisCurrent,
  PreparationTypes,
  SampleStateType,
} from "@app/types/samples.types";
import { useEquipment } from "@services/servicesGenericsTables";
import useTabs from "@app/pages/samples/utils/useTab";
import { useSample } from "@app/pages/samples/utils/useSample";
import useAuth from "@app/context/AuthContext";
import useAnalysis from "@app/pages/samples/utils/useAnalysis";
import { formatDateOrNow } from "@app/utils/formatDate";
import { setsAnalysisPhasesBPP } from "@app/pages/samples/config/phases/AnalysisPhases";
import useFilteredProcedures from "@app/pages/samples/hook/useFilteredProcedures";

const getDefaultValues = (
  currentPreparationIndex?: PreparationTypes,
  currentCounting?: AnalysisCurrent,
  currentCalculation?: AnalysisCurrent,
  sampling?: SampleStateType,
  user?: UserType,
  filteredProceduresCON?: ProcedureType,
  filteredCalculationCAL?: ProcedureType,
  idMuestra?: string,
  isFirstChange: boolean = true
) => {
  const currentSampling = sampling?.current;
  const currentCountingIndex =
    currentPreparationIndex?.countings?.[currentCounting?.index as number];
  const currentCalculationIndex =
    currentCountingIndex?.calculations[currentCalculation?.index as number];

  const currentSample = useSample.getState().get(Number(idMuestra));

  const consultEquipment = (id: number) => {
    return useEquipment
      .getState()
      .equipmentList.find((equipment) => equipment.id === id);
  };

  const processingCurrent =
    useSample.getState().stateSample?.processing?.current || [];

  const dryingProcessing =
    processingCurrent && processingCurrent.length > 0
      ? processingCurrent.find((item) => item.processing_category?.id === 3)
      : undefined;

  return {
    analysis_analyst: currentPreparationIndex?.analyst || user,
    //preparation
    //counting
    counting_analyst: currentCountingIndex?.analyst || user,
    counting_procedure:
      currentCountingIndex?.counting_procedure || filteredProceduresCON,
    counting_equipment: currentCountingIndex?.equipment || undefined,
    counting_detector: currentCountingIndex?.detectors || undefined,
    amount_sample:
      currentCountingIndex?.amount_sample || currentSampling.amount_sample,
    counting_unit_types:
      currentCountingIndex?.unit_types || currentSampling.unit_types,
    first_counting_time: currentCountingIndex?.first_counting_time || " ",
    first_counting_cpm: currentCountingIndex?.first_counting_cpm || " ",
    second_counting_cpm: currentCountingIndex?.second_counting_cpm || " ",
    counting_valid: currentCountingIndex?.counting_valid || "NA",
    counting_date: currentCountingIndex?.counting_date || dayjs().toISOString(),
    isFirstChange,
    //calculation
    start_date_time: formatDateOrNow(currentSampling?.date_start_sampling),
    end_date_time: formatDateOrNow(currentSampling?.date_end_sampling),
    calculation_amount_sample:
      currentPreparationIndex?.amount_sample || currentSampling.amount_sample,
    calculation_units:
      currentPreparationIndex?.unit_types || currentSampling.unit_types,
    calculation_tsie_sample: dryingProcessing?.wet_dry,
    calculation_shared: currentSample?.shared ? "Si" : "No",
    laboratory: currentCalculationIndex?.laboratory || DEFAULT_LABORATORY,
    calculation_procedure:
      currentCalculationIndex?.calculation_procedure || filteredCalculationCAL,
    calculation_equipment: currentCalculationIndex?.equipment
      ? Array.isArray(currentCalculationIndex?.equipment)
        ? currentCalculationIndex?.equipment
        : consultEquipment(Number(currentCalculationIndex?.equipment))
      : currentCountingIndex?.equipment,
    calculation_detector: currentCountingIndex?.detectors || [],
    calculation_fondo: currentCalculationIndex?.fondos || undefined,
    fondo_alfa: currentCalculationIndex?.fondo_alfa || undefined,
    calculation_date: formatDateOrNow(
      currentCalculationIndex?.calculation_date
    ),
    idResultPreload: currentCalculationIndex?.result?.id || undefined,
  };
};

const BPPForm = () => {
  const { tabValueAnalysis } = useTabs();

  const form = useFormContext();
  const { idMuestra } = useParams();
  const { stateSample } = useSample();
  const { sampling } = stateSample;
  const { user } = useAuth();
  const filteredProceduresCON = useFilteredProcedures("CON");
  const filteredCalculationCAL = useFilteredProcedures("CAL");

  const {
    currentPreparation,
    preparationsList,
    currentCounting,
    currentCalculation,
  } = useAnalysis();

  const currentPreparationObject = preparationsList.filter((preparation) =>
    preparation.analysis_type.some(
      (analysis) => analysis.id === form.getValues("typeAnalysis")?.id
    )
  )[currentPreparation.index as number];
  useEffect(() => {
    if (currentPreparationObject) {
      const currentValues = form.getValues();

      const newValues = {
        ...currentValues,
        ...getDefaultValues(
          currentPreparationObject,
          currentCounting,
          currentCalculation,
          sampling,
          user,
          filteredProceduresCON?.[0],
          filteredCalculationCAL?.[0],
          idMuestra
        ),
      };

      form.reset(newValues);
    }
  }, [idMuestra, JSON.stringify(currentPreparationObject), currentCounting]);

  const CurrentComponent = setsAnalysisPhasesBPP[tabValueAnalysis]?.component;
  return CurrentComponent ? <CurrentComponent /> : null;
};

export default BPPForm;
